import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Inbox, PackageSearch, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { usePageTitle } from "@/contexts/PageTitleContext";
import { EmptyState } from "@/components/shared/EmptyState";
import { PageErrorBoundary } from "@/components/fallbacks";
import { trpc } from "@/lib/trpc";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { ClientOrderStatusRow } from "@threadheads/shared";
import {
  ClientOrderStatusFilter,
  type ClientOrderStatusFilterValue,
} from "./components/ClientOrderStatusFilter";
import { ClientOrderCard } from "./components/ClientOrderCard";
import { ClientOrderDetailPane } from "./components/ClientOrderDetailPane";
import { PageLoader } from "@/components/loading/PageLoader";
import {
  InboxPager,
  InboxSplitView,
} from "@/features/order-messages/InboxSplitView";
import { useInboxLayout } from "@/features/order-messages/useInboxLayout";

const PAGE_SIZE = 50;

type SortMode = "newest" | "oldest";

/**
 * BLU-665 — two-pane inbox (approved Layout C) for the pure-client Orders
 * surface. Left: a 460px card list with its own toolbar (search, wrapping
 * status filters, sort) and scroll. Right: the selected order's detail pane.
 * The page itself never scrolls — each pane scrolls independently inside the
 * viewport remainder under the TopHeader. Below 820px the panes swap to a
 * single-pane push flow (list → detail with a Back button).
 */
function ClientOrdersPage() {
  const { t } = useTranslation("clientOrders");

  usePageTitle(t("page.title"), t("page.subtitle"));

  const [statusFilter, setStatusFilter] =
    useState<ClientOrderStatusFilterValue>("all");
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState<SortMode>("newest");
  const [selectedOrder, setSelectedOrder] =
    useState<ClientOrderStatusRow | null>(null);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);

  // Two-pane vs single-pane (measured container width; see useInboxLayout).
  const layout = useInboxLayout();

  // Reset to the first page whenever the filters change so we never land on an
  // out-of-range offset.
  useEffect(() => {
    setOffset(0);
  }, [statusFilter, search]);

  const listQuery = trpc.clientOrders.list.useQuery(
    {
      status: statusFilter === "all" ? undefined : statusFilter,
      search: search || undefined,
      limit: PAGE_SIZE,
      offset,
    },
    {
      staleTime: 15_000,
      // Keep the previous page visible while the next page / filter loads so
      // the list doesn't flash back to a skeleton on every pagination click.
      placeholderData: (previous) => previous,
    },
  );

  const orders = listQuery.data?.orders;

  // BLU-665 Phase 3 — per-order unread counts + last-message previews for the
  // visible page (60s background cadence; the OPEN thread itself polls at 30s
  // inside OrderChatPanel).
  const pageOrderIds = useMemo(
    () =>
      (orders ?? [])
        .map((o) => o.orderId)
        .filter((id): id is string => id != null),
    [orders],
  );
  const inboxMetaQuery = trpc.orderMessages.inboxMeta.useQuery(
    { orderIds: pageOrderIds },
    {
      refetchInterval: 60_000,
      refetchIntervalInBackground: false,
      staleTime: 30_000,
    },
  );
  const inboxMeta = inboxMetaQuery.data;

  // The RPC returns newest-first; "oldest" reverses within the loaded page
  // (the list is one page deep — a server sort param is not worth the backend
  // change this frontend-only phase deliberately avoids).
  const sortedOrders = useMemo(() => {
    if (!orders) return [];
    if (sort === "newest") return orders;
    return [...orders].reverse();
  }, [orders, sort]);

  // Deep link (?order=<shopifyOrderId>) — the target a notification's
  // app-relative link lands on. Honored once, on the first page of data that
  // contains the order.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandled = useRef(false);

  // Default-select the first selectable order on load, and re-anchor when the
  // current selection drops out of the visible page (filter/search/page move).
  useEffect(() => {
    if (!orders) return;
    if (!deepLinkHandled.current) {
      const param = searchParams.get("order");
      const wanted = param ? Number(param) : NaN;
      if (Number.isFinite(wanted)) {
        const target = orders.find((o) => o.shopifyOrderId === wanted);
        if (target) {
          deepLinkHandled.current = true;
          setSelectedOrder(target);
          setMobileDetailOpen(true);
          setSearchParams(
            (params) => {
              params.delete("order");
              return params;
            },
            { replace: true },
          );
          return;
        }
      }
    }
    setSelectedOrder((current) => {
      if (current) {
        const refreshed = orders.find(
          (o) => o.shopifyOrderId === current.shopifyOrderId,
        );
        if (refreshed) return refreshed;
      }
      return orders.find((o) => o.shopifyOrderId != null) ?? null;
    });
    // searchParams/setSearchParams intentionally omitted — the deep link is a
    // mount-time, handle-once concern; re-running on param identity churn
    // would fight user selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  const pagination = listQuery.data?.pagination;
  const total = pagination?.total ?? 0;

  const filtersActive = statusFilter !== "all" || search.length > 0;
  const showSkeleton = listQuery.isLoading;

  const selectOrder = (order: ClientOrderStatusRow) => {
    setSelectedOrder(order);
    setMobileDetailOpen(true);
  };

  const clearFilters = () => {
    setStatusFilter("all");
    setSearchInput("");
  };

  const toolbar = (
    <>
      <div className="relative">
        <Search
          className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          placeholder={t("search.placeholder")}
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          className="h-8 pl-9 text-xs"
          aria-label={t("search.ariaLabel")}
        />
      </div>
      <ClientOrderStatusFilter
        value={statusFilter}
        onChange={setStatusFilter}
      />
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t("list.count", { count: total })}
        </span>
        <SegmentedControl
          ariaLabel={t("sort.ariaLabel")}
          value={sort}
          onChange={setSort}
          options={[
            { value: "newest", label: t("sort.newest") },
            { value: "oldest", label: t("sort.oldest") },
          ]}
        />
      </div>
    </>
  );

  const list = listQuery.isError ? (
    <div className="p-4">
      <EmptyState
        icon={PackageSearch}
        title={t("empty.error.title")}
        description={t("empty.error.description")}
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => void listQuery.refetch()}
          >
            {t("empty.error.action")}
          </Button>
        }
      />
    </div>
  ) : showSkeleton ? (
    <PageLoader pool="orders" />
  ) : sortedOrders.length === 0 ? (
    <div className="p-4">
      {filtersActive ? (
        <EmptyState
          icon={PackageSearch}
          title={t("empty.filtered.title")}
          description={t("empty.filtered.description")}
          action={
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              {t("empty.filtered.action")}
            </Button>
          }
        />
      ) : (
        <EmptyState
          icon={Inbox}
          title={t("empty.none.title")}
          description={t("empty.none.description")}
        />
      )}
    </div>
  ) : (
    <ul>
      {sortedOrders.map((order, index) => (
        <li
          key={
            order.shopifyOrderId ??
            order.orderId ??
            order.orderNumber ??
            `row-${offset + index}`
          }
        >
          <ClientOrderCard
            order={order}
            selectable={order.shopifyOrderId != null}
            selected={
              order.shopifyOrderId != null &&
              order.shopifyOrderId === selectedOrder?.shopifyOrderId
            }
            onSelect={() => selectOrder(order)}
            unreadCount={
              order.orderId
                ? inboxMeta?.unreadByOrderId[order.orderId] ?? 0
                : 0
            }
            lastMessage={
              order.orderId
                ? inboxMeta?.lastMessageByOrderId[order.orderId] ?? null
                : null
            }
          />
        </li>
      ))}
    </ul>
  );

  return (
    <InboxSplitView
      layout={layout}
      listAriaLabel={t("list.ariaLabel")}
      detailAriaLabel={t("detail.paneAria")}
      toolbar={toolbar}
      list={list}
      footer={
        <InboxPager
          offset={offset}
          pageSize={PAGE_SIZE}
          total={total}
          hasMore={pagination?.hasMore ?? false}
          disabled={listQuery.isFetching}
          onPrevious={() => setOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
          onNext={() => setOffset((prev) => prev + PAGE_SIZE)}
        />
      }
      isFetching={listQuery.isFetching}
      isLoading={listQuery.isLoading}
      detailOpen={mobileDetailOpen}
      renderDetail={({ twoPane }) => (
        <ClientOrderDetailPane
          order={selectedOrder}
          onBack={() => setMobileDetailOpen(false)}
          showBack={!twoPane}
          unreadCount={
            selectedOrder?.orderId
              ? inboxMeta?.unreadByOrderId[selectedOrder.orderId] ?? 0
              : 0
          }
        />
      )}
    />
  );
}

export default function ClientOrdersPageWithErrorBoundary() {
  return (
    <PageErrorBoundary>
      <ClientOrdersPage />
    </PageErrorBoundary>
  );
}
